import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const PreferencesSettings = () => {
  const [preferences, setPreferences] = useState({
    // Trading Preferences
    defaultOrderType: 'limit',
    defaultTimeInForce: 'GTC',
    confirmationDialogs: true,
    autoRefreshInterval: 5,
    
    // Chart Preferences
    chartTheme: 'dark',
    defaultTimeframe: '1h',
    technicalIndicators: ['MA', 'RSI', 'MACD'],
    chartType: 'candlestick',
    
    // Notification Preferences
    emailNotifications: {
      orderFilled: true,
      priceAlerts: true,
      accountSecurity: true,
      marketNews: false,
      promotions: false
    },
    pushNotifications: {
      orderFilled: true,
      priceAlerts: true,
      accountSecurity: true,
      marketNews: false
    },
    smsNotifications: {
      orderFilled: false,
      priceAlerts: true,
      accountSecurity: true
    },
    
    // Display Preferences
    currency: 'USD',
    numberFormat: 'US',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    theme: 'light',
    
    // Privacy Preferences
    showPortfolioValue: true,
    showTradingHistory: false,
    allowDataCollection: true,
    marketingEmails: false,
    
    // API Keys
    coinMarketCapApiKey: ''
  });
  
  // Load saved preferences and API keys from localStorage on component mount
  useEffect(() => {
    const savedCmpApiKey = localStorage.getItem('cmpApiKey');
    if (savedCmpApiKey) {
      setPreferences(prev => ({
        ...prev,
        coinMarketCapApiKey: savedCmpApiKey
      }));
    }
  }, []);

  const orderTypes = [
    { value: 'market', label: 'Market Order' },
    { value: 'limit', label: 'Limit Order' },
    { value: 'stop', label: 'Stop Order' },
    { value: 'stop-limit', label: 'Stop-Limit Order' }
  ];

  const timeInForceOptions = [
    { value: 'GTC', label: 'Good Till Canceled' },
    { value: 'IOC', label: 'Immediate or Cancel' },
    { value: 'FOK', label: 'Fill or Kill' },
    { value: 'DAY', label: 'Day Order' }
  ];

  const refreshIntervals = [
    { value: 1, label: '1 second' },
    { value: 5, label: '5 seconds' },
    { value: 10, label: '10 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 60, label: '1 minute' }
  ];

  const chartThemes = [
    { value: 'light', label: 'Light Theme' },
    { value: 'dark', label: 'Dark Theme' },
    { value: 'auto', label: 'Auto (System)' }
  ];

  const timeframes = [
    { value: '1m', label: '1 Minute' },
    { value: '5m', label: '5 Minutes' },
    { value: '15m', label: '15 Minutes' },
    { value: '1h', label: '1 Hour' },
    { value: '4h', label: '4 Hours' },
    { value: '1d', label: '1 Day' }
  ];

  const technicalIndicatorOptions = [
    { value: 'MA', label: 'Moving Average' },
    { value: 'EMA', label: 'Exponential Moving Average' },
    { value: 'RSI', label: 'Relative Strength Index' },
    { value: 'MACD', label: 'MACD' },
    { value: 'BB', label: 'Bollinger Bands' },
    { value: 'STOCH', label: 'Stochastic' }
  ];

  const currencies = [
    { value: 'USD', label: 'US Dollar (USD)' },
    { value: 'EUR', label: 'Euro (EUR)' },
    { value: 'GBP', label: 'British Pound (GBP)' },
    { value: 'JPY', label: 'Japanese Yen (JPY)' },
    { value: 'CAD', label: 'Canadian Dollar (CAD)' }
  ];

  const handlePreferenceChange = (category, key, value) => {
    if (category) {
      setPreferences(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [key]: value
        }
      }));
    } else {
      setPreferences(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const handleIndicatorToggle = (indicator) => {
    setPreferences(prev => ({
      ...prev,
      technicalIndicators: prev.technicalIndicators.includes(indicator)
        ? prev.technicalIndicators.filter(i => i !== indicator)
        : [...prev.technicalIndicators, indicator]
    }));
  };

  const handleSavePreferences = () => {
    console.log('Saving preferences:', preferences);
    // Save CoinMarketCap API key to localStorage
    if (preferences.coinMarketCapApiKey) {
      localStorage.setItem('cmpApiKey', preferences.coinMarketCapApiKey);
    }
    // Handle save logic for other preferences
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading-semibold text-text-primary mb-1">Trading Preferences</h2>
          <p className="text-sm text-text-secondary">Customize your trading experience and platform settings</p>
        </div>
        
        <button
          onClick={handleSavePreferences}
          className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-trading hover:bg-primary-600 transition-colors duration-trading-fast"
        >
          <Icon name="Save" size={16} />
          <span className="font-body-medium">Save Changes</span>
        </button>
      </div>

      {/* API Key Settings */}
      <div className="mb-8 p-6 border border-border rounded-trading">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-6">API Key Settings</h3>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              CoinMarketCap API Key
            </label>
            <div className="flex">
              <input
                type="password"
                value={preferences.coinMarketCapApiKey}
                onChange={(e) => handlePreferenceChange(null, 'coinMarketCapApiKey', e.target.value)}
                className="flex-1 px-3 py-2 border border-border rounded-l-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
                placeholder="Enter your CoinMarketCap API key"
              />
              <button 
                className="px-4 py-2 bg-primary text-white rounded-r-trading hover:bg-primary-600 transition-colors duration-trading-fast"
                onClick={() => {
                  if (preferences.coinMarketCapApiKey) {
                    localStorage.setItem('cmpApiKey', preferences.coinMarketCapApiKey);
                    alert('CoinMarketCap API key saved!');
                  }
                }}
              >
                Save Key
              </button>
            </div>
            <p className="text-xs text-text-secondary mt-1">
              Get your API key from <a href="https://coinmarketcap.com/api/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CoinMarketCap</a>. Required for live cryptocurrency data.
            </p>
          </div>
        </div>
      </div>

      {/* Trading Preferences */}
      <div className="mb-8 p-6 border border-border rounded-trading">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-6">Trading Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Default Order Type
            </label>
            <select
              value={preferences.defaultOrderType}
              onChange={(e) => handlePreferenceChange(null, 'defaultOrderType', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
            >
              {orderTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Default Time in Force
            </label>
            <select
              value={preferences.defaultTimeInForce}
              onChange={(e) => handlePreferenceChange(null, 'defaultTimeInForce', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
            >
              {timeInForceOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Auto Refresh Interval
            </label>
            <select
              value={preferences.autoRefreshInterval}
              onChange={(e) => handlePreferenceChange(null, 'autoRefreshInterval', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
            >
              {refreshIntervals.map(interval => (
                <option key={interval.value} value={interval.value}>{interval.label}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-1">
                Order Confirmation Dialogs
              </label>
              <p className="text-xs text-text-secondary">Show confirmation before placing orders</p>
            </div>
            <button
              onClick={() => handlePreferenceChange(null, 'confirmationDialogs', !preferences.confirmationDialogs)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-trading-fast ${
                preferences.confirmationDialogs ? 'bg-primary' : 'bg-secondary-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-trading-fast ${
                  preferences.confirmationDialogs ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Chart Preferences */}
      <div className="mb-8 p-6 border border-border rounded-trading">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-6">Chart Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Chart Theme
            </label>
            <select
              value={preferences.chartTheme}
              onChange={(e) => handlePreferenceChange(null, 'chartTheme', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
            >
              {chartThemes.map(theme => (
                <option key={theme.value} value={theme.value}>{theme.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Default Timeframe
            </label>
            <select
              value={preferences.defaultTimeframe}
              onChange={(e) => handlePreferenceChange(null, 'defaultTimeframe', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
            >
              {timeframes.map(timeframe => (
                <option key={timeframe.value} value={timeframe.value}>{timeframe.label}</option>
              ))}
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-body-medium text-text-primary mb-3">
              Default Technical Indicators
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {technicalIndicatorOptions.map(indicator => (
                <label key={indicator.value} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.technicalIndicators.includes(indicator.value)}
                    onChange={() => handleIndicatorToggle(indicator.value)}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary-100"
                  />
                  <span className="text-sm text-text-primary">{indicator.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="mb-8 p-6 border border-border rounded-trading">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-6">Notification Settings</h3>
        
        <div className="space-y-6">
          {/* Email Notifications */}
          <div>
            <h4 className="font-body-medium text-text-primary mb-4">Email Notifications</h4>
            <div className="space-y-3">
              {Object.entries(preferences.emailNotifications).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-body-medium text-text-primary capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </label>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('emailNotifications', key, !value)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-trading-fast ${
                      value ? 'bg-primary' : 'bg-secondary-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-trading-fast ${
                        value ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Push Notifications */}
          <div>
            <h4 className="font-body-medium text-text-primary mb-4">Push Notifications</h4>
            <div className="space-y-3">
              {Object.entries(preferences.pushNotifications).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-body-medium text-text-primary capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </label>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('pushNotifications', key, !value)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-trading-fast ${
                      value ? 'bg-primary' : 'bg-secondary-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-trading-fast ${
                        value ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* SMS Notifications */}
          <div>
            <h4 className="font-body-medium text-text-primary mb-4">SMS Notifications</h4>
            <div className="space-y-3">
              {Object.entries(preferences.smsNotifications).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-body-medium text-text-primary capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </label>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('smsNotifications', key, !value)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-trading-fast ${
                      value ? 'bg-primary' : 'bg-secondary-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-trading-fast ${
                        value ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Display Preferences */}
      <div className="mb-8 p-6 border border-border rounded-trading">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-6">Display Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Base Currency
            </label>
            <select
              value={preferences.currency}
              onChange={(e) => handlePreferenceChange(null, 'currency', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
            >
              {currencies.map(currency => (
                <option key={currency.value} value={currency.value}>{currency.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Date Format
            </label>
            <select
              value={preferences.dateFormat}
              onChange={(e) => handlePreferenceChange(null, 'dateFormat', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
            >
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Time Format
            </label>
            <select
              value={preferences.timeFormat}
              onChange={(e) => handlePreferenceChange(null, 'timeFormat', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
            >
              <option value="12h">12 Hour</option>
              <option value="24h">24 Hour</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Theme
            </label>
            <select
              value={preferences.theme}
              onChange={(e) => handlePreferenceChange(null, 'theme', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="auto">Auto</option>
            </select>
          </div>
        </div>
      </div>

      {/* Privacy Settings */}
      <div className="p-6 border border-border rounded-trading">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-6">Privacy Settings</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-1">
                Show Portfolio Value
              </label>
              <p className="text-xs text-text-secondary">Display portfolio value in public areas</p>
            </div>
            <button
              onClick={() => handlePreferenceChange(null, 'showPortfolioValue', !preferences.showPortfolioValue)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-trading-fast ${
                preferences.showPortfolioValue ? 'bg-primary' : 'bg-secondary-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-trading-fast ${
                  preferences.showPortfolioValue ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-1">
                Show Trading History
              </label>
              <p className="text-xs text-text-secondary">Allow others to view your trading history</p>
            </div>
            <button
              onClick={() => handlePreferenceChange(null, 'showTradingHistory', !preferences.showTradingHistory)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-trading-fast ${
                preferences.showTradingHistory ? 'bg-primary' : 'bg-secondary-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-trading-fast ${
                  preferences.showTradingHistory ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-1">
                Allow Data Collection
              </label>
              <p className="text-xs text-text-secondary">Help improve our services with anonymous usage data</p>
            </div>
            <button
              onClick={() => handlePreferenceChange(null, 'allowDataCollection', !preferences.allowDataCollection)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-trading-fast ${
                preferences.allowDataCollection ? 'bg-primary' : 'bg-secondary-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-trading-fast ${
                  preferences.allowDataCollection ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-1">
                Marketing Emails
              </label>
              <p className="text-xs text-text-secondary">Receive promotional emails and market updates</p>
            </div>
            <button
              onClick={() => handlePreferenceChange(null, 'marketingEmails', !preferences.marketingEmails)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-trading-fast ${
                preferences.marketingEmails ? 'bg-primary' : 'bg-secondary-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-trading-fast ${
                  preferences.marketingEmails ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreferencesSettings;